import React, { Ref, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Filter as _Filter } from './HeaderFilter';
import { getCellValue, load } from './helpers';

import { faEraser, faFilter, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useModalManager from '@hooks/useModalManager';
import useResizeObserver from '@hooks/useResizeObserver';
import { AsyncListData, useAsyncList } from '@react-stately/data';
import { SortDescriptor } from '@react-types/shared';
import { SpectrumColumnProps } from '@react-types/table';
import { Button, StyledInputProps } from '@ui';
import Dropdown, { DropdownItemProps } from '@ui/Dropdown';
import Table, { TableBodyProps, TableProps } from '@ui/Table';
import Tooltip from '@ui/Tooltip';
import useTempAuth from 'components/auth/TempAuthProvider/useTempAuth';
import { difference, kebabCase, sortBy } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import { cn } from '~/lib/cn';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { FilterComponent, FilterSelection } from './Filter';
import FilterContext from './Filter/FilterContext';
import FilterRenderer from './Filter/FilterRenderer';
import useDataTableState from './hooks/useDataTableState';
import useSmartFilterValueStore from './hooks/useSmartFilterValueStore';
import SelectionAssistant from './SelectionAssistant';
import SmartFilterComponent from './SmartFilterComponent/SmartFilterComponent';
import Statistics from './Statistics';
import { Statistic } from './types';

export type Filter = _Filter;

export type Selection = string[] | 'all';

export type AsyncListRef = AsyncListData<any>;

export interface FilterState {
    only_selected?: string[];
    [key: string]:
        | { value: string | number | null }
        | { from: string | number | null; to: string | number | null }
        | string
        | string[]
        | null;
}

type TableColumnConfig<T = any> = Omit<SpectrumColumnProps<T>, 'showDivider'> & {
    css?: Record<string, any>;
};

export interface Column<T = any> extends Partial<TableColumnConfig<T>> {
    name: string;
    uid: string;
    /** @deprecated */
    cellCss?: any;
    renderer?: (value: any, item?: T) => any;
}

export interface Action {
    key: string;
    label: string;
    action: ({
        selection,
        setSelection,
        filter
    }?: {
        selection: Selection;
        setSelection?: React.Dispatch<React.SetStateAction<Selection>>;
        filter?: FilterState;
    }) => void;
    description?: string;
    icon?: IconDefinition;
    divider?: boolean;
    color?: DropdownItemProps['color'];
}

export type ActionCallbackProps = Parameters<Action['action']>[0];

interface DataTableProps extends Omit<Partial<TableProps>, 'onSelectionChange'> {
    onSelectionChange?: (keys: Selection, items: any[]) => any;

    /** @deprecated */
    css?: any;
    /** @deprecated */
    containerCss?: any;
    /** @deprecated */
    striped?: any;
}

type ColumnValue<T, K extends keyof T> = T[K];

interface Props {
    initialFilterState?: FilterState;
    /**
     * Which persisted filter state this table belongs to.
     *
     * Defaults to the endpoint, which is right for the usual case — one screen
     * per list — and wrong wherever two tables read the same endpoint for
     * different jobs. Those two then share one store: opening the second
     * inherits the first's filter, and every change in it re-filters the first
     * one live and writes that to localStorage. Name the second one and they
     * stop overhearing each other.
     */
    stateKey?: string;
    onFilterChange?: (filter: FilterState) => void;
    /**
     * How many rows the current filter matches, whenever that number changes.
     *
     * The table has always known it — it is what `meta.total` off the response
     * feeds and what the built-in statistics line prints. This only hands it
     * out, for a screen that shows its own summary above the table and would
     * otherwise be quoting the unfiltered figure beside a filtered list.
     */
    onTotalChange?: (total: number) => void;
    filters?: Filter[];
    searchInputProps?: Partial<StyledInputProps>;
    smartFilterKeys?: ColumnValue<Filter, 'filterKey'>[];
    fetchUrl: string;
    hideSelectAll?: boolean;
    fetchMethod?: 'GET' | 'POST';
    fetchData?: object;
    customActionButton?: React.ReactNode;
    actions?: Action[];
    actionsLabel?: string;
    actionsLoading?: boolean;
    columns: (Column | false)[];
    hideSearchBar?: boolean;
    showStatistic?: boolean;
    asyncListRef?: ((ref) => void) | any;
    cellRenderer?: (item: any, key: any) => React.ReactNode | null;
    rowActions?: (item: any, key?: any) => React.ReactNode | string | null;
    tableProps?: DataTableProps;
    tableBodyProps?: Partial<TableBodyProps>;
    emptyRenderer?: React.JSX.Element;
    flush?: boolean;
    className?: string;
    /** Default (initial) sort applied on first load, sent to the API as the `sort` param. */
    initialSortDescriptor?: SortDescriptor;
    sortingFn?: (a: any, b: any) => number;
    keyResolver?: (item: any) => string;
    fullTextValue?: string;
    ListEmptyComponent?: React.ReactNode;
    hideSelectionAssistant?: boolean;
    rowClassName?: (item: any) => string | undefined;

    /** @deprecated */
    css?: any;
}

const DataTable = ({
    onFilterChange,
    onTotalChange,
    initialFilterState,
    stateKey,
    filters: _filters,
    smartFilterKeys,
    fetchUrl,
    fetchData,
    fetchMethod,
    hideSearchBar = false,
    ListEmptyComponent,
    columns: _columns,
    asyncListRef,
    showStatistic,
    cellRenderer,
    rowActions,
    tableProps: { defaultSelectedKeys, selectedKeys: controlledSelectedKeys, ...tableProps } = {},
    tableBodyProps,
    searchInputProps,
    emptyRenderer,
    customActionButton,
    actions,
    className,
    actionsLoading,
    actionsLabel,
    initialSortDescriptor,
    sortingFn = undefined,
    keyResolver,
    fullTextValue,
    hideSelectionAssistant,
    rowClassName
}: Props) => {
    const { t } = useTranslation('datatable');
    const resolvedActionsLabel = actionsLabel ?? t('actions.bulkLabel');
    const [nextTableRef, { height }] = useResizeObserver();
    const { addModal, removeModal } = useModalManager();
    const applyFilterState = useSmartFilterValueStore((state) => state.applyFilterState);
    const { filterState, setFilterState, setEnabledFilterState, enabledFilterState } = useDataTableState(
        stateKey ?? kebabCase(fetchUrl)
    )();
    const debouncedFullTextValue = useDebouncedValue(fullTextValue, 500);
    const filters = useMemo(() => sortBy(_filters, 'filterName'), [_filters]);

    const columns = useMemo(() => {
        const mappedColumns = _columns
            .filter((c) => c !== false)
            .map((col: Column) => ({ ...col, ...(filters?.find((f) => f.filterKey === col.uid) || {}) }));

        if (!!rowActions && mappedColumns.every((c) => c.uid !== 'actions')) {
            mappedColumns.push({
                uid: 'actions',
                name: t('actions.column'),
                align: 'end'
            });
        }

        return mappedColumns;
    }, [_columns, filters, rowActions, t]);

    const [filter, _setFilter] = useState<FilterState>(initialFilterState || filterState || {});
    const [showsSelection, setShowsSelection] = useState(false);
    const setFilter = useCallback(
        (nextFilter: FilterState) => {
            _setFilter(nextFilter);
            setFilterState(nextFilter);
            applyFilterState(nextFilter);
            if (onFilterChange) onFilterChange(nextFilter);
        },
        [onFilterChange]
    );
    const [selection, setSelection] = useState<Selection>(
        (defaultSelectedKeys
            ? defaultSelectedKeys === 'all'
                ? defaultSelectedKeys
                : Array.from(defaultSelectedKeys)
            : []) as Selection
    );
    // A controlled selection reset from outside must reach the internal copy too,
    // the action menu and the selection assistant read that one.
    useEffect(() => {
        if (undefined === controlledSelectedKeys) return;
        const next = 'all' === controlledSelectedKeys ? 'all' : Array.from(controlledSelectedKeys);
        setSelection((current) =>
            JSON.stringify(current === 'all' ? current : Array.from(current)) === JSON.stringify(next)
                ? current
                : (next as Selection)
        );
    }, [controlledSelectedKeys]);
    const [enabledFilters, setEnabledFilters] = useState<string[]>(enabledFilterState);
    const [statistics, setStatistics] = useState<Statistic>({
        totalItems: 0,
        selectedItems: 0,
        additionalInfo: null
    });
    // Whether a response has ever set the statistics. The initial zero above is
    // a placeholder, not a count, and must not be reported as one: a screen
    // whose filter is restored from storage would otherwise show "0 Treffer"
    // for the length of the first request.
    const statisticsArrived = useRef(false);
    const updateStatistics = (stats: Partial<Statistic>) => {
        statisticsArrived.current = true;
        setStatistics((prev) => ({
            ...prev,
            ...stats
        }));
    };
    // Reported out of an effect rather than from inside `updateStatistics`, so
    // the parent's setState never runs inside this component's own state
    // update. The ref keeps the latest callback without making it a dependency.
    const reportTotal = useRef(onTotalChange);
    reportTotal.current = onTotalChange;
    useEffect(() => {
        if (statisticsArrived.current) reportTotal.current?.(statistics.totalItems);
    }, [statistics.totalItems]);
    const { token } = useTempAuth();
    const list = useAsyncList<any, string>({
        load: load(fetchUrl, fetchData, fetchMethod, updateStatistics, token),
        initialFilterText: JSON.stringify(filter),
        initialSortDescriptor
    });

    const safeItems = list.items ?? [];

    // useAsyncList starts out 'idle' and only dispatches its first load in an effect,
    // so on the very first render the list looks loaded-and-empty. Wait for a real
    // load before believing that, otherwise the empty state flashes up every time.
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    useEffect(() => {
        if (list.isLoading) setHasLoadedOnce(true);
    }, [list.isLoading]);

    // Memoize itemByKey map for performance
    const itemByKey = useMemo(() => {
        const map = new Map<string, any>();
        for (const item of safeItems) {
            const key = String(keyResolver ? keyResolver(item) : item?.id);
            if (key === 'undefined') {
                continue;
            }
            map.set(key, item);
        }
        return map;
    }, [safeItems, keyResolver]);

    // Memoize sorted items for performance
    const sortedItems = useMemo(() => {
        if (!sortingFn) {
            return safeItems;
        }

        return [...safeItems].sort(sortingFn);
    }, [safeItems, sortingFn]);

    // Memoize row actions by key for performance
    const rowActionsByKey = useMemo(() => {
        if (!rowActions) {
            return null;
        }

        const map = new Map<string, React.ReactNode>();
        for (const item of safeItems) {
            const key = String(keyResolver ? keyResolver(item) : item?.id);
            if (key === 'undefined') {
                continue;
            }
            map.set(key, rowActions(item, 'actions'));
        }
        return map;
    }, [safeItems, rowActions, keyResolver]);

    if (typeof asyncListRef === 'function') asyncListRef(list);
    // eslint-disable-next-line react-hooks/immutability
    else if (asyncListRef) asyncListRef.current = list;

    const mappedColumns = useMemo(() => {
        const filteredColumns = columns.filter((c) => c !== false);

        // add column for row actions if not already present
        if (!!rowActions && filteredColumns.every((c) => c.uid !== 'actions')) {
            filteredColumns.push({
                uid: 'actions',
                name: t('actions.column'),
                align: 'end'
            });
        }

        return filteredColumns;
    }, [columns, rowActions, t]);

    // update full text filter debounced on typing
    useEffect(() => {
        if (debouncedFullTextValue) _setFilter({ ...filter, full_text: debouncedFullTextValue });
    }, [debouncedFullTextValue]);

    // sync local UI state with the persisted store after hydration/navigation
    useEffect(() => {
        if (initialFilterState) return;
        _setFilter(filterState || {});
        applyFilterState(filterState || {});
        onFilterChange?.(filterState || {});
    }, [applyFilterState, filterState, initialFilterState, onFilterChange]);

    useEffect(() => {
        setEnabledFilters(enabledFilterState || []);
    }, [enabledFilterState]);

    const showSelection = useCallback(
        (state: boolean) => {
            setShowsSelection((prev) => {
                if (selection === 'all') return state;
                if (isEmpty(filter.only_selected) === isEmpty(selection) && prev === state) return state;

                if (state) {
                    setFilter({
                        ...filter,
                        only_selected: selection
                    });
                } else {
                    setFilter({
                        ...filter,
                        only_selected: undefined
                    });
                }

                return state;
            });
        },
        [filter, selection]
    );

    const showPossibleFilters = () => {
        addModal({
            title: t('filters.configure'),
            content: (id) => (
                <FilterSelection
                    filters={filters.filter((f) => !(smartFilterKeys ?? []).includes(f.filterKey))}
                    enabledFilters={enabledFilters}
                    callback={(values) => {
                        removeModal(id);
                        setFilter(
                            difference(enabledFilters, values).reduce(
                                (carry, current) => ({ ...carry, [current]: undefined }),
                                filter
                            )
                        );
                        setEnabledFilterState(values);
                        setEnabledFilters(values);
                    }}
                />
            ),
            show: true
        });
    };

    useEffect(() => {
        list.setFilterText(JSON.stringify(filter));
    }, [filter]);

    useEffect(() => {
        list.reload();
        setSelection(
            (defaultSelectedKeys
                ? defaultSelectedKeys === 'all'
                    ? defaultSelectedKeys
                    : Array.from(defaultSelectedKeys)
                : []) as Selection
        );
    }, [fetchUrl]);

    useEffect(() => {
        updateStatistics({ selectedItems: selection === 'all' ? 'all' : (selection?.length ?? 0) });
        if (selection === 'all' || selection.length === 0) {
            showSelection(false);
        }
    }, [selection]);

    return !emptyRenderer || list.isLoading || safeItems.length > 0 ? (
        <div
            className={cn(
                'tw-relative tw-flex tw-flex-1 tw-flex-col tw-gap-2 tw-overflow-hidden tw-rounded-xl',
                className
            )}
        >
            {safeItems.length <= 0 && hasLoadedOnce && !list.isLoading && ListEmptyComponent && (
                <>
                    <div
                        className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-z-40 tw-rounded-xl tw-bg-black/5 tw-backdrop-blur-md"
                        style={{ height: height + (tableProps?.borderWeight === 'light' ? 2 : 4) }}
                    />
                    <div
                        className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-rounded-2xl"
                        style={{ height: height + (tableProps?.borderWeight === 'light' ? 2 : 4) }}
                    >
                        {ListEmptyComponent}
                    </div>
                </>
            )}
            <FilterContext.Provider value={[filter, setFilter]}>
                {/* The toolbar exists for the search box, the list's own button and
                    the bulk-action menu — any of the three. It used to exist for the
                    search box alone, so a list that hid the search bar also lost its
                    bulk actions: rows could be selected, the assistant counted them,
                    and there was nothing to do with the selection. */}
                {(!hideSearchBar || customActionButton || actions?.length > 0) && (
                    <>
                        {/* Wraps rather than overflows: the search box takes all
                            the width it is given, so in a narrow column a
                            labelled action button beside it was pushed off the
                            edge instead of onto its own line. */}
                        <div
                            className={cn(
                                'tw-flex tw-flex-nowrap tw-items-center tw-gap-2',
                                hideSearchBar && 'tw-justify-end'
                            )}
                        >
                            {!hideSearchBar && <FilterComponent searchInputProps={searchInputProps} />}

                            {/* Side by side, not either/or. A list can have both a
                                primary button of its own and bulk actions, and
                                hiding the menu behind the button meant a list
                                that passed both silently lost its actions. */}
                            {customActionButton}
                            {actions?.length > 0 && (
                                <Dropdown label={resolvedActionsLabel} isLoading={actionsLoading} size="sm">
                                    {actions
                                        .filter((a) => a !== null)
                                        .map((action) => (
                                            <Dropdown.Item
                                                icon={action.icon && <FontAwesomeIcon icon={action.icon} />}
                                                key={action.key}
                                                color={action.color}
                                                label={action.label}
                                                onClick={() => action.action({ selection, setSelection, filter })}
                                            />
                                        ))}
                                </Dropdown>
                            )}
                        </div>
                        {!hideSearchBar && (
                            <SmartFilterComponent
                                onFilterChange={setFilter}
                                filter={filter}
                                filters={filters?.filter((filterConfig) =>
                                    smartFilterKeys?.includes(filterConfig.filterKey)
                                )}
                            />
                        )}
                    </>
                )}
                <FilterRenderer filters={filters} enabledFilters={enabledFilters} />

                {/* @ts-ignore */}
                {showStatistic && <Statistics sortDescriptor={list.sortDescriptor} statistic={statistics} />}
                <Table
                    className="tw-inline-block tw-flex tw-h-full tw-w-full tw-flex-col"
                    ref={nextTableRef as Ref<HTMLTableElement>}
                    aria-label="Tabelle"
                    containerClassName="tw-flex-1"
                    onSortChange={list.sort}
                    // @ts-ignore
                    sortDescriptor={list.sortDescriptor}
                    rowClassName={rowClassName}
                    {...tableProps}
                    selectedKeys={controlledSelectedKeys ?? selection}
                    onSelectionChange={(keys) => {
                        if (keys === 'all') {
                            setSelection(keys);
                            if (tableProps?.onSelectionChange) {
                                tableProps.onSelectionChange(keys, safeItems);
                            }
                        } else {
                            const keysArray = Array.from(keys) as string[];
                            setSelection(keysArray);
                            if (tableProps?.onSelectionChange) {
                                const selectedItems = keysArray
                                    .map((key) => itemByKey.get(String(key)))
                                    .filter(Boolean);
                                tableProps.onSelectionChange(keysArray, selectedItems);
                            }
                        }
                    }}
                >
                    <Table.Header columns={mappedColumns}>
                        {(column) => (
                            <Table.Column key={column.uid} {...column}>
                                {column.uid === 'actions' && filters?.length > 0 ? (
                                    <div
                                        className={cn(
                                            column.align === 'start' && 'tw-right-0',
                                            column.align === 'end' && 'tw-left-0',
                                            'tw-absolute tw-bottom-0 tw-top-0 tw-z-50 tw-ml-2 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4'
                                        )}
                                    >
                                        <Tooltip
                                            tooltip={
                                                smartFilterKeys?.length
                                                    ? t('filters.selectAdditional')
                                                    : t('filters.select')
                                            }
                                        >
                                            <Button
                                                color="secondary"
                                                size="xs"
                                                onPress={() => {
                                                    showPossibleFilters();
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faFilter} />
                                            </Button>
                                        </Tooltip>
                                        <Tooltip tooltip={t('filters.clear')}>
                                            <Button
                                                color="secondary"
                                                size="xs"
                                                onPress={() => {
                                                    setFilter({});
                                                    setEnabledFilterState([]);
                                                    setEnabledFilters([]);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faEraser} />
                                            </Button>
                                        </Tooltip>
                                    </div>
                                ) : null}
                                {column.name}
                            </Table.Column>
                        )}
                    </Table.Header>

                    <Table.Body
                        {...tableBodyProps}
                        items={sortedItems}
                        loadingState={list.isLoading ? 'loading' : list.loadingState}
                        onLoadMore={list.loadMore}
                    >
                        {(item: any) => (
                            <Table.Row key={keyResolver ? keyResolver(item) : item?.id}>
                                {(key) => (
                                    <Table.Cell>
                                        {key === 'actions'
                                            ? rowActionsByKey?.get(String(keyResolver ? keyResolver(item) : item?.id))
                                            : cellRenderer?.(item, key) || getCellValue(columns)(item, key)}
                                    </Table.Cell>
                                )}
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
                {!hideSelectionAssistant && (
                    <SelectionAssistant
                        statistics={statistics}
                        selection={selection}
                        showSelection={showsSelection}
                        setShowSelection={showSelection}
                        handleDeselectAll={() => {
                            setSelection([]);
                            updateStatistics({ selectedItems: 0 });
                            tableProps?.onSelectionChange?.([], []);
                        }}
                    />
                )}
            </FilterContext.Provider>
        </div>
    ) : (
        emptyRenderer
    );
};

export default DataTable;
